// ASISTENCIA DE LA PERSONA — pestaña «Asistencia» de /profesores/:id (Mabel 19-oct-2026: «como la tienen los
// alumnos… igual para el staff»). Solo lectura.
//   · Maestros: cada clase de su HORARIO del mes con su estado. Sale de la MISMA cuenta que Finanzas › Nómina
//     (`computePayroll` en el backend): «Vino» = la clase no se marcó «No vino» ni «Suplente» (así se paga).
//     Se marca en Pasar lista (Maestra/o) o en Finanzas › Nómina.
//   · Staff y maestros: faltas / permisos / retardos que se REGISTRAN aquí mismo (TeacherIncidencias, Mabel 19-oct-2026:
//     «los retardos prefiero se puedan realizar desde el perfil y no en finanzas»). Solo registro, no descuenta. Antes
//     este bloque leía Nómina › Ajustes (letsmove_payroll_adjustments): se reemplazó.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarCheck, Check, X, Repeat, ClipboardX, UserCheck } from 'lucide-react';
import TeacherIncidencias from './TeacherIncidencias';
import { getTeacherAttendance, TeacherAttendanceData, AsistSesion } from '../api';
import { readCache, writeCache, pruneCacheGroup } from '../../../services/localCache';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';

const pad = (n: number) => String(n).padStart(2, '0');
const fromISO = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); };
const isoDe = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mesActual = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const rangoMes = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return { from: `${ym}-01`, to: m === 12 ? `${y + 1}-01-01` : `${y}-${pad(m + 1)}-01` };
};
const moverMes = (ym: string, delta: number) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + delta, 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const nombreMes = (ym: string) => { const t = fromISO(`${ym}-01`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }); return t.charAt(0).toUpperCase() + t.slice(1); };
const dia = (d: string) => { const t = fromISO(d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }); return t.charAt(0).toUpperCase() + t.slice(1); };
const corto = (d: string) => fromISO(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
/** Lunes de la semana de `d` (la nómina va lunes→domingo). */
const lunesDe = (d: string) => { const x = fromISO(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return isoDe(x); };
const finSemana = (lunes: string) => { const x = fromISO(lunes); x.setDate(x.getDate() + 6); return isoDe(x); };

const ESTADO: Record<AsistSesion['estado'], { label: string; cls: string; Icon: typeof Check }> = {
  vino: { label: 'Vino', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', Icon: Check },
  no_vino: { label: 'No vino', cls: 'bg-red-500/15 text-red-600 dark:text-red-400', Icon: X },
  suplente: { label: 'Con suplente', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-400', Icon: Repeat },
  suplio: { label: 'Suplió', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300', Icon: UserCheck },
};

function Tile({ label, value, sub, tone = '' , testid }: { label: string; value: string | number; sub?: string; tone?: string; testid?: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-line px-2.5 py-1.5 sm:px-3 sm:py-2" data-testid={testid}>
      <div className="truncate text-[11px] text-content-3">{label}</div>
      <div className={`font-display text-base font-bold leading-tight sm:text-lg ${tone || 'text-content'}`}>{value}</div>
      {sub && <div className="truncate text-[10px] text-content-3 sm:text-[11px]">{sub}</div>}
    </div>
  );
}

export default function TeacherAsistencia({ userId, esProfe }: { userId: number; esProfe: boolean }) {
  const [ym, setYm] = useState(mesActual);
  const { from, to } = rangoMes(ym);
  const key = `teacher-asist:${userId}:${from}`;
  const [data, setData] = useState<TeacherAttendanceData | null>(() => readCache<TeacherAttendanceData>(key) ?? null);
  const [estado, setEstado] = useState<'ok' | 'cargando' | 'err' | 'pronto'>('cargando');

  useEffect(() => {
    let vivo = true;
    if (!esProfe) return;   // el staff no tiene clases: solo el bloque de faltas/permisos/retardos
    setData(readCache<TeacherAttendanceData>(key) ?? null);
    setEstado('cargando');
    getTeacherAttendance(userId, from, to)
      .then((r) => { if (!vivo) return; setData(r.data); setEstado('ok'); writeCache(key, r.data); pruneCacheGroup(`teacher-asist:${userId}`, key); })
      .catch((e: any) => { if (!vivo) return; setEstado(e?.status === 404 ? 'pronto' : 'err'); });
    return () => { vivo = false; };
  }, [userId, from, to, key, esProfe]);

  // Semanas lun→dom del mes, de la más reciente a la más vieja.
  const semanas = useMemo(() => {
    if (!data) return [];
    const by = new Map<string, AsistSesion[]>();
    for (const s of data.sesiones) { const k = lunesDe(s.date); if (!by.has(k)) by.set(k, []); by.get(k)!.push(s); }
    // Primero las semanas que ya empezaron (la más reciente arriba); las que aún no llegan, al final y en orden.
    const empezo = (ses: AsistSesion[]) => ses.some((s) => !s.futura);
    return [...by.entries()].sort((a, b) => {
      const ea = empezo(a[1]), eb = empezo(b[1]);
      if (ea !== eb) return ea ? -1 : 1;
      return ea ? (a[0] < b[0] ? 1 : -1) : (a[0] < b[0] ? -1 : 1);
    });
  }, [data]);

  const esteMes = ym === mesActual();
  const selector = (
    <div className="flex items-center gap-1" data-testid="asist-mes">
      <button onClick={() => setYm(moverMes(ym, -1))} aria-label="Mes anterior"
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-content-2 transition hover:bg-surface-2"><ChevronLeft size={16} /></button>
      <span className="min-w-[8.5rem] text-center text-sm font-semibold text-content">{nombreMes(ym)}</span>
      <button onClick={() => setYm(moverMes(ym, 1))} aria-label="Mes siguiente" disabled={esteMes}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-content-2 transition hover:bg-surface-2 disabled:opacity-40"><ChevronRight size={16} /></button>
      {!esteMes && <button onClick={() => setYm(mesActual())} className="ml-1 rounded-lg px-2 py-1 text-xs font-medium text-accent-ink hover:underline">Este mes</button>}
    </div>
  );

  const incidencias = <TeacherIncidencias userId={userId} esProfe={esProfe} from={from} to={to} mesTexto={nombreMes(ym).toLowerCase()} />;

  let cuerpo: JSX.Element;
  if (!esProfe) {
    // STAFF: no hay checador ni horario de clases; sus faltas/permisos/retardos se registran aquí (cualquiera que vea
    // la ficha los ve; capturar = quien puede editar la ficha del staff).
    cuerpo = incidencias;
  } else if (!data && estado === 'cargando') {
    cuerpo = <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  } else if (!data && estado === 'pronto') {
    cuerpo = <div className="rounded-xl bg-surface-2/60 p-3 text-sm text-content-3">Esta sección se está activando. Vuelve a abrirla en unos minutos.</div>;
  } else if (!data) {
    cuerpo = <div className="rounded-xl bg-surface-2/60 p-3 text-sm text-content-3">No se pudo cargar la asistencia.</div>;
  } else {
    const r = data.resumen;
    cuerpo = (
      <>
        {/* Faltas/permisos/retardos ARRIBA, antes de la lista de clases del mes (Mabel 19-oct-2026: quedaba hasta abajo). */}
        {incidencias}
        <div className="mb-2 grid grid-cols-3 gap-1.5 sm:gap-2 xl:grid-cols-6">
          <Tile label="Dio" value={`${r.vino} de ${r.programadas}`} sub={r.proximas ? `+ ${r.proximas} próximas este mes` : undefined} tone="text-emerald-700 dark:text-emerald-400" testid="asist-dio" />
          <Tile label="No vino" value={r.no_vino} tone={r.no_vino ? 'text-red-600 dark:text-red-400' : ''} testid="asist-novino" />
          <Tile label="Con suplente" value={r.con_suplente} tone={r.con_suplente ? 'text-sky-700 dark:text-sky-400' : ''} />
          <Tile label="Sin lista" value={r.sin_lista} sub="dadas sin lista" tone={r.sin_lista ? 'text-amber-600 dark:text-amber-400' : ''} testid="asist-sinlista" />
          <Tile label="Suplió" value={r.suplio} sub="a otro maestro" />
          <Tile label="Horas" value={r.horas} sub="= Nómina" testid="asist-horas" />
        </div>
        <p className="mb-3 text-[11px] text-content-3">
          «Vino» = clase de su horario que no se marcó <b>No vino</b> ni <b>Suplente</b> (así se le paga en Nómina). Se marca en
          Pasar lista (tarjeta de la maestra/o) o en Finanzas › Nómina.
        </p>
        {semanas.length === 0 ? (
          <EmptyState compact icon={CalendarCheck} title="Sin clases en este mes" subtitle="No tiene clases de horario ni suplencias en estas fechas." />
        ) : (
          <div className="space-y-3">
            {semanas.map(([lunes, ses]) => {
              const pas = ses.filter((s) => !s.futura);
              const suyas = pas.filter((s) => s.estado !== 'suplio');
              const dio = pas.filter((s) => s.estado === 'vino').length;
              const sinLista = pas.filter((s) => (s.estado === 'vino' || s.estado === 'suplio') && !s.has_list).length;
              return (
                <div key={lunes} className="rounded-xl border border-line" data-testid="asist-semana">
                  <div className={`flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded-t-xl bg-surface-2/60 px-3 py-2 ${pas.length ? 'border-b border-line' : 'rounded-b-xl'}`}>
                    <span className="text-sm font-semibold text-content">Semana {corto(lunes)} – {corto(finSemana(lunes))}</span>
                    <span className="text-xs text-content-2">
                      {pas.length ? <>dio <b>{dio}</b> de {suyas.length}</> : `${ses.length} clase${ses.length === 1 ? '' : 's'} programada${ses.length === 1 ? '' : 's'}`}
                      {sinLista > 0 && <span className="text-amber-600 dark:text-amber-400"> · {sinLista} sin lista</span>}
                      {pas.length > 0 && ses.length > pas.length && <span className="text-content-3"> · {ses.length - pas.length} próxima{ses.length - pas.length === 1 ? '' : 's'}</span>}
                    </span>
                  </div>
                  {pas.length === 0 ? null : (
                  <div className="divide-y divide-line/60">
                    {ses.map((s) => {
                      const e = ESTADO[s.estado];
                      return (
                        <div key={`${s.date}-${s.class_id}-${s.start_time}`} className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${s.futura ? 'opacity-60' : ''}`}>
                          <span className="w-[6.5rem] shrink-0 text-content-2">{dia(s.date)}</span>
                          <span className="w-11 shrink-0 tabular-nums text-content-3">{s.start_time || '—'}</span>
                          <Link to={`/clases/${s.class_id}`} className="min-w-0 flex-1 truncate font-medium text-content hover:underline">{s.class_name}</Link>
                          <span className="flex flex-wrap items-center gap-1.5">
                            {s.futura ? (
                              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-semibold text-content-3">Próxima</span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.cls}`}><e.Icon size={11} /> {e.label}</span>
                            )}
                            {!s.futura && (s.estado === 'vino' || s.estado === 'suplio') && !s.has_list && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400" title="Se dio pero no se pasó lista"><ClipboardX size={11} /> sin lista</span>
                            )}
                            {!s.scheduled && <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-content-3">fuera de horario</span>}
                          </span>
                          {(s.substitute_name || s.substitute_for || (s.marked_by && s.estado !== 'vino') || s.note) && (
                            <div className="basis-full pl-0 text-[11px] text-content-3 sm:pl-[10.25rem]">
                              {s.substitute_name && <>La dio <b className="text-content-2">{s.substitute_name}</b>. </>}
                              {s.substitute_for && <>Cubrió a <b className="text-content-2">{s.substitute_for}</b>. </>}
                              {s.marked_by && s.estado !== 'vino' && <>Marcó {s.marked_by}. </>}
                              {s.note && <>{s.note}</>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm" data-testid="profe-asistencia">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-2">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content">
          <CalendarCheck size={13} className="text-accent-ink" /> Asistencia
          {estado === 'cargando' && data && <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-content-3">actualizando…</span>}
        </h3>
        {selector}
      </div>
      {cuerpo}
    </div>
  );
}
